import { Typography } from '@mui/material'
import { DateTime, DateTimeFormatOptions } from 'luxon'
import React from 'react'

import { GridColDef, GridRenderCellParams, Tooltip } from '../../../../components'
import { COLORS, styled } from '../../../../theme'
import { ActionsCellMenu } from '../components'

/* eslint-disable import/no-unused-modules */

/**
 * Represents the data structure for a list field.
 */
export type ListFieldData = {
  /**
   * The title of the list field.
   */
  title?: string
  /**
   * The column definitions for the list field.
   */
  columns: ListFieldColumnDefinition[]
  /**
   * The row definitions for the list field.
   */
  rows: ListFieldRowDefinition[]
}

/**
 * Represents the definition of a column in a list field.
 */
export type ListFieldColumnDefinition = {
  /**
   * The name of the column.
   */
  name: string
  /**
   * The label of the column.
   */
  label: string
  /**
   * The type of the column.
   * Possible values: 'string', 'number', 'date', 'datetime', 'select', 'state', 'customState', 'actions'.
   */
  type: 'string' | 'number' | 'date' | 'datetime' | 'select' | 'state' | 'customState' | 'actions'
  /**
   * The options for the column (only applicable for 'select' type).
   */
  options?: string[]
  /**
   * Specifies if the column is mandatory.
   */
  mandatory?: boolean
}

/**
 * Definition of a row in a ListField component.
 */
export interface ListFieldRowDefinition {
  id: string
  editCallBack?: (id: string) => () => void
  deleteCallBack?: (id: string) => () => void
  [key: string]: string | number | string[] | ((id: string) => void) | undefined
}

/**
 * Represents the state of a dialog.
 */
export type DialogState = {
  open: boolean // Indicates whether the dialog is open or not.
  itemId: string | number | undefined // The ID of the item associated with the dialog.
}

/**
 * Represents the state of an alert.
 */
export type AlertState = {
  message: string // The message to be displayed in the alert.
  visible: boolean // Indicates whether the alert is visible or not.
}

/**
 * Sets the default date format options to avoid differences in locale date formats.
 * This avoids some formats where only 1 digit is used for the day or month
 */

export const DATE_FORMAT_OPTIONS: DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}

const StyledTypography = styled(Typography)`
  color: ${COLORS.error.main};
`

type RowFieldOutputValue = Omit<ListFieldRowDefinition, 'editCallBack' | 'deleteCallBack'>

export interface OnChangeCustomEvent {
  type: 'CREATE' | 'UPDATE' | 'DELETE'
  value: RowFieldOutputValue
}

/**
 * Converts the Api columns to GridColDef that can be used for the DataGrid component rendering
 * @param columns The Api columns
 * @returns The GridColDef
 * @category ListField
 */
export function convertColumns(columns: ListFieldColumnDefinition[]): GridColDef<any>[] {
  return columns.map((column) => {
    /**
     * Default grid column definition
     */
    const gridColDef: GridColDef<any> = {
      field: column.name,
      type: column.type === 'customState' || column.type === 'select' ? 'string' : column.type,
      headerName: column.label,
      width: column.type === 'customState' ? 10 : undefined,
      editable: false,
    }

    switch (column.type) {
      case 'date':
        gridColDef.flex = 1
        gridColDef.valueFormatter = (params) => {
          return DateTime.fromISO(params.value).toLocaleString(DATE_FORMAT_OPTIONS)
        }
        break

      // case 'datetime':
      //   gridColDef.flex = 1
      //   gridColDef.valueGetter = (params) => {
      //     const date = DateTime.fromISO(params.row[column.name] as string)
      //     return date.toLocaleString(DateTime.DATETIME_SHORT)
      //   }
      //   break

      case 'customState':
        gridColDef.renderCell = (params) => {
          let tooltipTitle = ''
          let backgroundColor = ''
          let border = ''
          let width = '10px'
          let height = '10px'

          const state = params.row[column.name] as string

          if (state === 'active') {
            tooltipTitle = 'Active'
            backgroundColor = 'green'
          } else if (state === 'planned') {
            tooltipTitle = 'Planned'
            backgroundColor = 'white'
            border = '2px solid green'
            height = '6px'
            width = '6px'
          } else {
            tooltipTitle = 'Finished'
            backgroundColor = 'gray'
          }

          return (
            <Tooltip title={tooltipTitle}>
              <div
                style={{
                  width,
                  height,
                  borderRadius: '50%',
                  backgroundColor,
                  border,
                }}
              ></div>
            </Tooltip>
          )
        }
        break

      case 'select':
        gridColDef.flex = 1
        gridColDef.valueGetter = (params) => {
          return params.value
        }
        break

      case 'actions':
        gridColDef.width = 10
        gridColDef.align = 'right'
        gridColDef.renderCell = (params: GridRenderCellParams<any, Element>) => (
          <ActionsCellMenu
            key={params.id}
            editCallBack={() => params.row.editCallBack(params.id)}
            deleteCallback={() => params.row.deleteCallBack(params.id)}
            id={params.row.id as string}
          />
        )
        break

      // case 'number':
      //   gridColDef.flex = 1
      //   break
      case 'string':
        gridColDef.flex = 1
        break

      default:
        gridColDef.flex = 1
        gridColDef.renderCell = () => <StyledTypography variant="body1">Invalid Data❗️</StyledTypography>
    }

    return gridColDef
  })
}
